import './order-step.scss';

import { useContext, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import FormComments from '@/components/Shared/entities/form-comments';
import { ROUTES } from '@/components/Shared/routes/routes';
import { AdaptiveFormSteps, Button, FormSteps } from '@/components/Shared/ui';

import { CreateTabContext, IeLeasingOrderContext } from '../IeOrder';
import { REQUIRED_FIELDS } from './constants';
import { useIeLeasingCreate, useIeLeasingForm, useStepCount } from './hooks';
import { FirstStep, SecondStep, ThirdStep } from './widgets';

const STEPS: React.ReactNode[] = [
  <FirstStep key="first-step" />,
  <SecondStep key="second-step" />,
  <ThirdStep key="third-step" />
];

const OrderStep = () => {
  const navigate = useNavigate();

  const { isActionsDisabled } = useContext(IeLeasingOrderContext);
  const { isFieldsFrozen } = useContext(CreateTabContext);

  const [currentStep, setCurrentStep] = useState(0);

  const methods = useIeLeasingForm();
  const { stepCountItems } = useStepCount({ control: methods.control });

  const { isCreateLoading, onSubmit } = useIeLeasingCreate({
    currentStep,
    setCurrentStep,
    trigger: methods.trigger
  });

  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);

  const modalCancelHandler = () => {
    setShowCancelModal(false);
    navigate(ROUTES.home);
  };

  const onCancelHandler = () => {
    setShowCancelModal(true);
  };

  const handleStep = async (val: number) => {
    if (currentStep === val) return;

    let isValid = true;
    if (!isFieldsFrozen && val > currentStep) {
      let iteration = -1;

      while (iteration < val && isValid) {
        iteration++;
        isValid =
          (await methods.trigger(REQUIRED_FIELDS[iteration])) ||
          iteration === val;
      }
      if (!isValid) {
        setCurrentStep(iteration);
        if (iteration < val) {
          setTimeout(() => {
            methods.trigger(REQUIRED_FIELDS[iteration]);
          }, 10);
        }
      }
    }

    if (val < currentStep) {
      methods.clearErrors();
      setCurrentStep(val);
    } else if (val > currentStep && isValid) {
      methods.clearErrors();
      setCurrentStep(val);
    }
  };

  return (
    <>
      <div className="ie-order-step">
        <div className="ie-order-step__side-container-adaptive">
          <AdaptiveFormSteps
            currentStep={currentStep + 1}
            steps={stepCountItems}
            onChange={handleStep}
          />
        </div>

        {!isActionsDisabled && (
          <div className="ie-order-step__additional-actions-container-adaptive">
            <FormComments type="ie-leasing" isAdaptive />
          </div>
        )}

        <FormProvider {...methods}>
          <form
            className="ie-order-step__form"
            onSubmit={(e) => e.preventDefault()}
          >
            {STEPS.map((step, index) => (
              <div
                key={index}
                style={{ display: currentStep === index ? 'block' : 'none' }}
              >
                {step}
              </div>
            ))}

            {!isFieldsFrozen && (
              <Button
                className="ie-order-step__submit"
                variant="filled-red"
                disabled={Object.values(methods.formState.errors).length !== 0}
                isLoading={isCreateLoading}
                onClick={() => onSubmit(methods.getValues())}
              >
                {currentStep !== 2 ? 'Продолжить' : 'Создать заявку'}
              </Button>
            )}
          </form>
        </FormProvider>

        <div className="ie-order-step__side-container">
          <FormSteps
            currentStep={currentStep + 1}
            steps={stepCountItems}
            onChange={handleStep}
            onCancel={!isFieldsFrozen ? onCancelHandler : undefined}
          />
          {!isActionsDisabled && <FormComments type="ie-leasing" />}
        </div>
      </div>

      <CustomModal
        editClass="ie-order-step-cancel"
        title="Вы хотите отменить создание заявки?"
        isModalOpen={showCancelModal}
        handleOk={modalCancelHandler}
        handleCancel={() => setShowCancelModal(false)}
        footer={[
          <Button
            key="back"
            onClick={() => setShowCancelModal(false)}
            disabled={isActionsDisabled}
          >
            Нет
          </Button>,
          <Button
            variant="filled-red"
            key="submit"
            onClick={modalCancelHandler}
            disabled={isActionsDisabled}
          >
            Да, отменить
          </Button>
        ]}
      >
        <p className="ie-order-step-cancel__text">
          Внесенные данные не будут сохранены
        </p>
      </CustomModal>
    </>
  );
};

export default OrderStep;
